import React, { useState, useEffect, useCallback } from 'react';
import { publicFetch } from '../utils/publicApi';

const API_BASE = 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

export default function PublicCourtRoster({ token, weekOffset }) {
  const [assignments, setAssignments] = useState([]);
  const [totalPay, setTotalPay] = useState(0);
  const [attendedPay, setAttendedPay] = useState(0);
  const [cutoffHours, setCutoffHours] = useState(24);
  const [responseEnabled, setResponseEnabled] = useState(true);
  const [payVisible, setPayVisible] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const formatLocalDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const getDateRange = useCallback(() => {
    const today = new Date();
    const day = today.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diffToMonday + (weekOffset * 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return {
      startDate: formatLocalDate(monday),
      endDate: formatLocalDate(sunday)
    };
  }, [weekOffset]);

  useEffect(() => {
    const loadAssignments = async () => {
      try {
        setLoading(true);
        setError('');
        const { startDate, endDate } = getDateRange();
        const res = await publicFetch(`${API_BASE}/public/roster/court/${token}?startDate=${startDate}&endDate=${endDate}`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setAssignments(data.assignments || []);
          setTotalPay(data.totalPay || 0);
          setAttendedPay(data.attendedPay || 0);
          if (data.cutoffHours !== undefined) setCutoffHours(data.cutoffHours);
          if (data.responseEnabled !== undefined) setResponseEnabled(data.responseEnabled);
          if (data.payVisible !== undefined) setPayVisible(data.payVisible);
        } else {
          setError('Failed to load court roster');
        }
      } catch (err) {
        setError('Error loading court roster: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    loadAssignments();
  }, [token, weekOffset, getDateRange]);

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-AU', {
      weekday: 'short', day: 'numeric', month: 'short'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '-';
    const [h, m] = timeStr.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour % 12 || 12;
    return `${h12}:${m} ${ampm}`;
  };

  const isWithinCutoff = (assignment) => {
    // Use startTime on the assignment date, or start of day if no time
    const dateStr = assignment.date;
    if (assignment.startTime) {
      const [h, m] = assignment.startTime.split(':').map(Number);
      const assignmentTime = new Date(dateStr + 'T00:00:00');
      assignmentTime.setHours(h, m, 0, 0);
      const cutoffTime = new Date(assignmentTime.getTime() - cutoffHours * 60 * 60 * 1000);
      return new Date() >= cutoffTime;
    }
    // No time — use start of day
    const dayStart = new Date(dateStr + 'T00:00:00');
    const cutoffTime = new Date(dayStart.getTime() - cutoffHours * 60 * 60 * 1000);
    return new Date() >= cutoffTime;
  };

  const canRespond = (assignment) => {
    if (assignment.isLocked) return false;
    if (assignment.isPast) return false;
    if (isWithinCutoff(assignment)) return false;
    return true;
  };

  const handleRespond = async (assignment, response) => {
    if (response === 'rejected') {
      if (!window.confirm('Are you sure you want to reject this shift?')) return;
    }
    try {
      const res = await publicFetch(`${API_BASE}/public/roster/court/${assignment.id}/respond/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ response })
      });
      if (res.ok) {
        setAssignments(prev => prev.map(a =>
          a.id === assignment.id ? { ...a, response } : a
        ));
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Failed to submit response');
      }
    } catch (err) {
      alert('Error submitting response: ' + err.message);
    }
  };

  const getRowClass = (assignment) => {
    if (assignment.response === 'rejected') return 'public-rejected-row';
    if (assignment.response === 'accepted') return 'public-accepted-row';
    if (assignment.isPast && assignment.isLocked && !assignment.attended) return 'public-unattended-row';
    return '';
  };

  // Group by date
  const groupedByDate = {};
  assignments.forEach(a => {
    if (!groupedByDate[a.date]) groupedByDate[a.date] = [];
    groupedByDate[a.date].push(a);
  });
  const sortedDates = Object.keys(groupedByDate).sort();

  return (
    <div>
      {error && <div className="public-tab-error">{error}</div>}

      {loading ? (
        <div className="public-tab-loading">Loading court roster...</div>
      ) : assignments.length === 0 ? (
        <div className="public-empty-state">
          <p>No court assignments for this period</p>
        </div>
      ) : (
        <>
          <div className="public-roster-table-scroll">
          <table className="public-roster-table">
            <thead>
              <tr>
                <th>Start Time</th>
                <th>End Time</th>
                <th>Role</th>
                {payVisible && <th>Pay</th>}
                <th>Status</th>
                {responseEnabled && <th>Response</th>}
              </tr>
            </thead>
            <tbody>
              {sortedDates.map(date => (
                <React.Fragment key={date}>
                  <tr className="public-date-group-header">
                    <td colSpan={(responseEnabled ? 1 : 0) + (payVisible ? 1 : 0) + 4}>{formatDate(date)}</td>
                  </tr>
                  {groupedByDate[date].map(assignment => (
                    <tr key={assignment.id} className={getRowClass(assignment)}>
                      <td>{formatTime(assignment.startTime)}</td>
                      <td>{formatTime(assignment.endTime)}</td>
                      <td>
                        <span className="public-role-badge court-official">
                          {assignment.qualification || 'Court Official'}
                        </span>
                      </td>
                      {payVisible && <td className="public-pay-cell">${assignment.pay.toFixed(2)}</td>}
                      <td>
                        {assignment.isPast && assignment.isLocked && !assignment.attended ? (
                          <span className="public-status-badge unattended">Unattended</span>
                        ) : assignment.attended ? (
                          <span className="public-status-badge attended">Attended</span>
                        ) : null}
                      </td>
                      {responseEnabled && <td>
                        {assignment.response === 'accepted' ? (
                          <span className="public-response-badge accepted">Accepted</span>
                        ) : assignment.response === 'rejected' ? (
                          <span className="public-response-badge rejected">Rejected</span>
                        ) : (
                          canRespond(assignment) ? (
                          <div className="public-response-buttons">
                            <button
                              className="public-btn-accept"
                              onClick={() => handleRespond(assignment, 'accepted')}
                              title="Accept this shift"
                            >&#10003;</button>
                            <button
                              className="public-btn-reject"
                              onClick={() => handleRespond(assignment, 'rejected')}
                              title="Reject this shift"
                            >&#10007;</button>
                          </div>
                          ) : null
                        )}
                      </td>}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          </div>
          {payVisible && (
            <div className="public-pay-summary">
              <div className="public-pay-item">
                <span>Forecast Pay:</span>
                <strong>${totalPay.toFixed(2)}</strong>
              </div>
              <div className="public-pay-item attended">
                <span>Attended Pay:</span>
                <strong>${attendedPay.toFixed(2)}</strong>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
